import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  InputLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { registerDevice, fetchSIMs } from "./api/machineApi";
import { useHistory } from "react-router-dom";

const theme = createTheme({
  components: {
    MuiTextField: { defaultProps: { size: "small" } },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          fontWeight: "bold",
          marginBottom: "4px",
          display: "block",
        },
      },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
      styleOverrides: { root: { fontSize: "0.75rem" } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: "0.75rem" } },
    },
    MuiButton: {
      defaultProps: { size: "small" },
      styleOverrides: { root: { fontSize: "0.75rem", padding: "4px 8px" } },
    },
  },
});

const StyledFormBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

const RequiredStar = styled("span")(({ theme }) => ({
  color: theme.palette.error.main,
}));

const RegisterDevice = () => {
  const [formData, setFormData] = useState({
    trackerDeviceId: "",
    trackerSimId: "",
    trackerDeviceBrand: "",
    trackerDeviceStatus: "",
    trackerDeviceManufacturingDate: "",
  });

  const history = useHistory();
  const [simOptions, setSimOptions] = useState([]);

  useEffect(() => {
    const loadSIMs = async () => {
      try {
        const sims = await fetchSIMs();
        setSimOptions(sims);
      } catch (error) {
        console.error("Failed to fetch SIMs:", error);
      }
    };

    loadSIMs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const result = await registerDevice(formData);
      console.log("Success:", result);
      alert("Device registered successfully!");
      history.push("/machine-overview");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to register device");
    }
  };

  const handleCancel = () => {
    setFormData({
      trackerDeviceId: "",
      trackerSimId: "",
      trackerDeviceBrand: "",
      trackerDeviceStatus: "",
      trackerDeviceManufacturingDate: "",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Box mt={3} mb={2}>
          <Typography variant="h5" component="h1" gutterBottom>
            Register Device
          </Typography>
          <StyledFormBox>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="trackerDeviceId">
                  Tracker Device ID <RequiredStar>*</RequiredStar>
                </InputLabel>
                <TextField
                  fullWidth
                  name="trackerDeviceId"
                  value={formData.trackerDeviceId}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="trackerSimId">
                  Tracker SIM ID <RequiredStar>*</RequiredStar>
                </InputLabel>
                <FormControl fullWidth required>
                  <Select
                    id="trackerSimId"
                    name="trackerSimId"
                    value={formData.trackerSimId}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {simOptions.map((sim) => (
                      <MenuItem key={sim.id} value={sim.id.toString()}>
                        {sim.simNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="trackerDeviceBrand">
                  Device Brand <RequiredStar>*</RequiredStar>
                </InputLabel>
                <TextField
                  fullWidth
                  name="trackerDeviceBrand"
                  value={formData.trackerDeviceBrand}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="trackerDeviceStatus">
                  Device Status <RequiredStar>*</RequiredStar>
                </InputLabel>
                <FormControl fullWidth required>
                  <Select
                    name="trackerDeviceStatus"
                    value={formData.trackerDeviceStatus}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="trackerDeviceManufacturingDate">
                  Manufacturing Date <RequiredStar>*</RequiredStar>
                </InputLabel>
                <TextField
                  fullWidth
                  type="date"
                  name="trackerDeviceManufacturingDate"
                  value={formData.trackerDeviceManufacturingDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Register
              </StyledButton>
            </Box>
          </StyledFormBox>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default RegisterDevice;
